import { t as uiText } from '../i18n';
import { getAuth } from 'firebase/auth';
import { getSpeechToTextEndpoint } from '../config/cloudRunConfig';

export type AdminSpeechToTextResponse =
  | {
      ok: true;
      provider: 'gpt-4o-mini-transcribe';
      transcript: string;
      language: string | null;
      meta?: {
        durationMs?: number | null;
        bytes?: number;
        tookMs?: number;
        estimatedCostUsd?: number | null;
        notreuToken?: number;
      };
      requestId?: string;
    }
  | {
      ok: false;
      error: { code: string; message: string };
      requestId?: string;
    };

function getSpeechToTextBaseUrl(): string {
  const url = getSpeechToTextEndpoint?.() || import.meta.env.VITE_SPEECH_TO_TEXT_URL;
  if (!url) return '';
  return String(url).replace(/\/$/, '');
}

export async function adminSpeechToText(params: {
  workspaceId: string;
  audioBase64: string;
  mimeType: string;
  durationMs?: number;
  language?: string;
}): Promise<AdminSpeechToTextResponse> {
  const TAG = '🎙️ [Admin STT API]';
  console.log(`${TAG} リクエスト開始`, {
    workspaceId: params.workspaceId,
    mimeType: params.mimeType,
    durationMs: params.durationMs,
    language: params.language,
    audioBase64Length: params.audioBase64?.length ?? 0,
  });

  const base = getSpeechToTextBaseUrl();
  if (!base) {
    console.error(`${TAG} ❌ エンドポイントURL未設定 (VITE_SPEECH_TO_TEXT_URL)`);
    return { ok: false, error: { code: 'CONFIG_ERROR', message: uiText("Speech recognition service is not configured") } };
  }
  console.log(`${TAG} エンドポイント: ${base}/admin-speech-to-text`);

  const auth = getAuth();
  const user = auth?.currentUser;
  if (!user) {
    console.error(`${TAG} ❌ 未ログイン状態（currentUser が null）`);
    return { ok: false, error: { code: 'AUTH_ERROR', message: uiText("Sign-in required") } };
  }
  console.log(`${TAG} ユーザー確認 OK: uid=${user.uid}`);

  let idToken: string;
  try {
    idToken = await user.getIdToken();
    console.log(`${TAG} Firebase IDトークン取得 OK`);
  } catch (e) {
    console.error(`${TAG} ❌ IDトークン取得失敗:`, e);
    return { ok: false, error: { code: 'AUTH_ERROR', message: uiText("Couldn't get the auth token") } };
  }

  try {
    console.log(`${TAG} fetch 送信中...`);
    const fetchStart = Date.now();
    const resp = await fetch(`${base}/admin-speech-to-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(params)
    });
    console.log(`${TAG} fetch 完了: status=${resp.status} (${Date.now() - fetchStart}ms)`);

    if (!resp.ok) {
      const errText = await resp.text().catch(() => uiText("(Failed to read body)"));
      console.error(`${TAG} ❌ HTTPエラー: status=${resp.status}`, errText);
    }

    const data = (await resp.json().catch((e) => {
      console.error(`${TAG} ❌ レスポンスJSON解析失敗:`, e);
      return null;
    })) as any;

    if (!data || typeof data.ok !== 'boolean') {
      console.error(`${TAG} ❌ レスポンス形式不正:`, data);
      return { ok: false, error: { code: 'BAD_RESPONSE', message: uiText("Invalid response from the speech recognition service") } };
    }

    if (data.ok) {
      console.log(`${TAG} ✅ 文字起こし成功:`, {
        transcript: data.transcript,
        language: data.language,
        meta: data.meta,
        requestId: data.requestId,
      });
    } else {
      console.warn(`${TAG} ⚠️ サーバーエラー応答:`, data.error, `requestId=${data.requestId}`);
    }

    return data as AdminSpeechToTextResponse;
  } catch (e) {
    console.error(`${TAG} ❌ ネットワークエラー:`, e);
    return { ok: false, error: { code: 'NETWORK_ERROR', message: uiText("A network error occurred") } };
  }
}
